import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoNotificationsOutline } from "react-icons/io5";
import { axiosConfig } from "../axiosAPI";
import "../Assets/Css/NotificationDropdown.css";

const PAGE_SIZE = 10;
const RIDE_LOOKUP_PAGE_SIZE = 100;

function getDetailsRoute(entityType, entityUuid) {
  if (entityType === "city_ride") {
    return { path: `/DetailsRequestsCity/${entityUuid}`, returnTo: "/Request_city" };
  }
  if (entityType === "ride") {
    return { path: `/DetailsRequests/${entityUuid}`, returnTo: "/Request" };
  }
  return null;
}

function formatNotificationDate(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function NotificationDropdown() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [navigatingUuid, setNavigatingUuid] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axiosConfig.get("/dashboard/notifications/unread-count");
      setUnreadCount(response.data.data ?? 0);
    } catch {
      // silently ignore — badge will stay at last known count
    }
  }, []);

  const loadNotifications = useCallback(async (page, append = false) => {
    if (page === 1) {
      setListLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = new URLSearchParams({
        pageNo: String(page),
        rowCount: String(PAGE_SIZE),
      });
      const response = await axiosConfig.get(`/dashboard/notifications?${params.toString()}`);
      const result = response.data.data?.result ?? [];
      const total = response.data.data?.totalCount ?? 0;

      setTotalCount(total);
      setPageNo(page);
      setNotifications((prev) => (append ? [...prev, ...result] : result));
    } catch {
      if (!append) {
        setNotifications([]);
        setTotalCount(0);
      }
    } finally {
      setListLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const fetchNotifications = useCallback(() => loadNotifications(1, false), [loadNotifications]);

  const fetchNextPage = useCallback(() => {
    if (listLoading || loadingMore || notifications.length >= totalCount) return;
    loadNotifications(pageNo + 1, true);
  }, [listLoading, loadingMore, notifications.length, totalCount, pageNo, loadNotifications]);

  const handleListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || listLoading || loadingMore || notifications.length >= totalCount) return;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (nearBottom) {
      fetchNextPage();
    }
  }, [listLoading, loadingMore, notifications.length, totalCount, fetchNextPage]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [isOpen, fetchNotifications, fetchUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAsRead = async (notificationUuid) => {
    try {
      await axiosConfig.put(`/dashboard/notifications/${notificationUuid}/read`);
      setNotifications((prev) =>
        prev.map((n) =>
          n.notificationUuid === notificationUuid ? { ...n, isRead: true } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // ignore
    }
  };

  const markAllAsRead = async () => {
    try {
      await axiosConfig.put("/dashboard/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  };

  const fetchRideItem = async (entityType, entityUuid) => {
    const endpoint =
      entityType === "city_ride" ? "/dashboard/city-rides" : "/dashboard/rides";
    let lookupPage = 1;

    try {
      while (true) {
        const params = new URLSearchParams({
          pageNo: String(lookupPage),
          rowCount: String(RIDE_LOOKUP_PAGE_SIZE),
        });
        const response = await axiosConfig.get(`${endpoint}?${params.toString()}`);
        const results = response.data.data?.result ?? [];
        const lookupTotal = response.data.data?.totalCount ?? 0;
        const found = results.find((r) => r.uuid === entityUuid);
        if (found) return found;
        if (lookupPage * RIDE_LOOKUP_PAGE_SIZE >= lookupTotal) break;
        lookupPage += 1;
      }
    } catch {
      // fall through to minimal item
    }

    return { uuid: entityUuid };
  };

  const handleNotificationClick = async (notification) => {
    const route = getDetailsRoute(notification.entityType, notification.entityUuid);
    if (!route || navigatingUuid) return;

    setNavigatingUuid(notification.notificationUuid);

    try {
      if (!notification.isRead) {
        await markAsRead(notification.notificationUuid);
      }

      const item = await fetchRideItem(notification.entityType, notification.entityUuid);
      setIsOpen(false);
      navigate(route.path, {
        state: { item, returnTo: location.pathname || route.returnTo },
      });
    } finally {
      setNavigatingUuid(null);
    }
  };

  const displayedNotifications =
    activeTab === "unread"
      ? notifications.filter((n) => !n.isRead)
      : notifications;

  return (
    <div className="notification-dropdown" ref={containerRef}>
      <button
        type="button"
        className="notification-bell-btn"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Notifications"
        disabled={Boolean(navigatingUuid)}
      >
        <IoNotificationsOutline size={22} />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-panel">
          {navigatingUuid && (
            <div className="notification-route-loading">
              <i className="pi pi-spin pi-spinner" />
              <span>Opening details...</span>
            </div>
          )}
          <div className="notification-panel-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                className="notification-mark-all-btn"
                onClick={markAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-tabs">
            <button
              type="button"
              className={`notification-tab${activeTab === "all" ? " active" : ""}`}
              onClick={() => setActiveTab("all")}
            >
              All
            </button>
            <button
              type="button"
              className={`notification-tab${activeTab === "unread" ? " active" : ""}`}
              onClick={() => setActiveTab("unread")}
            >
              Unread
              {unreadCount > 0 && (
                <span className="notification-tab-count">{unreadCount}</span>
              )}
            </button>
          </div>

          <div
            className={`notification-list${navigatingUuid ? " is-navigating" : ""}`}
            ref={listRef}
            onScroll={handleListScroll}
          >
            {listLoading ? (
              <div className="notification-empty">
                <i className="pi pi-spin pi-spinner" />
                <span>Loading...</span>
              </div>
            ) : displayedNotifications.length === 0 ? (
              <div className="notification-empty">
                {activeTab === "unread" ? "No unread notifications" : "No notifications"}
              </div>
            ) : (
              <>
                {displayedNotifications.map((notification) => {
                  const isNavigating = navigatingUuid === notification.notificationUuid;

                  return (
                    <button
                      key={notification.notificationUuid}
                      type="button"
                      className={`notification-item${notification.isRead ? " read" : " unread"}${isNavigating ? " navigating" : ""}`}
                      onClick={() => handleNotificationClick(notification)}
                      disabled={Boolean(navigatingUuid)}
                    >
                      {isNavigating ? (
                        <i className="pi pi-spin pi-spinner notification-item-spinner" />
                      ) : (
                        !notification.isRead && <span className="notification-unread-dot" />
                      )}
                      <div className="notification-item-content">
                        <div className="notification-item-header">
                          <span className="notification-title">{notification.title}</span>
                          <span className="notification-time">
                            {formatNotificationDate(notification.dateCreated)}
                          </span>
                        </div>
                        <p className="notification-body">{notification.content}</p>
                      </div>
                    </button>
                  );
                })}
                {loadingMore && (
                  <div className="notification-load-more">
                    <i className="pi pi-spin pi-spinner" />
                    <span>Loading more...</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationDropdown;
